import React, { useState } from 'react';
import './Chatbot.css';
import { queryPython } from './queryPython';

const MAX_LENGTH = 10000;

const truncateResponse = (text) => {
    return text.length > MAX_LENGTH ? text.slice(0, MAX_LENGTH) + '...' : text;
};

function Chatbot({ visible }) {
    const [isOpen, setIsOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);

    const toggleChatbot = () => {
        setIsOpen((prevOpen) => !prevOpen);
        setIsExpanded((prevExpanded) => !prevExpanded);
    };

    const handleInputChange = (event) => setInput(event.target.value);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!input.trim()) return;

        setMessages((prevMessages) => [...prevMessages, { text: input, type: 'user' }]);
        setInput('');

        try {
            setIsTyping(true);
            const data = await queryPython(input);
            const botMessage = cleanHtml(data.response);
            const truncatedMessage = truncateResponse(botMessage);

            setMessages((prevMessages) => [...prevMessages, { text: truncatedMessage, type: 'bot' }]);
        } catch (error) {
            console.error('Error:', error);
            setMessages((prevMessages) => [...prevMessages, { text: 'Sorry, something went wrong.', type: 'bot' }]);
        } finally {
            setIsTyping(false);
        }
    };

    const cleanHtml = (html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        let text = "";
        for (let node of doc.body.childNodes) {
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.nodeValue;
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                if (node.tagName === 'H1' || node.tagName === 'H2' || node.tagName === 'H3') {
                    text += '\n\n**' + node.textContent + '**\n';
                } else if (node.tagName === 'BR') {
                    text += '\n';
                } else if (node.tagName === 'LI') {
                    text += '\n- ' + node.textContent;
                } else if (node.tagName === 'A') {
                    text += '[' + node.textContent + '](' + node.href + ')';
                } else if (node.tagName === 'STRONG' || node.tagName === 'B') {
                    text += '**' + node.textContent + '**';
                } else {
                    text += cleanHtml(node.outerHTML);
                }
            }
        }
        return text.trim();
    };

    const renderFormattedMessage = (text) => {
        const lines = text.split('\n');
        
        return lines.map((line, lineIndex) => {
            if (line.trim() === '') {
                // Preserve empty lines
                return <div key={lineIndex}>&nbsp;</div>;
            }
    
            // Check if the line is a numbered list item
            const numberedListMatch = line.match(/^(\d+\.)\s(.+)/);
            if (numberedListMatch) {
                const [, number, content] = numberedListMatch;
                return (
                    <div key={lineIndex} className="numbered-list-item">
                        <span className="number">{number}</span>
                        <span className="content">{renderFormattedContent(content)}</span>
                    </div>
                );
            }
    
            // Check if the line is a demo link
            if (line.trim().startsWith('- Demo Link:')) {
                return (
                    <div key={lineIndex} className="demo-link">
                        {renderFormattedContent(line)}
                    </div>
                );
            }
    
            return <div key={lineIndex}>{renderFormattedContent(line)}</div>;
        });
    };

    const renderFormattedContent = (content) => {
        const parts = content.split(/(\*\*[^*]+\*\*|\[.+?\]\(.+?\))/g);
        return parts.map((part, partIndex) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={partIndex}>{part.slice(2, -2)}</strong>;
            } else if (part.match(/\[([^\]]+)\]\(([^)]+)\)/)) {
                const [, linkText, url] = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
                return <a key={partIndex} href={url} target="_blank" rel="noopener noreferrer">{linkText}</a>;
            } else {
                return <span key={partIndex}>{part}</span>;
            }
        });
    };

    return (
        <div className={`chatbot-container ${visible ? 'show' : ''} ${isOpen ? 'open' : ''} ${isExpanded ? 'expanded' : ''}`}>
            {!isOpen ? (
                <div className="chatbot-icon" onClick={toggleChatbot}>
                    <span role="img" aria-label="chat icon" className="icon-content">💬</span>
                </div>
            ) : (
                <>
                    <div className="chatbot-header">
                        <div className="chatbot-prompt">Chat with me</div>
                        <div className="chatbot-buttons">
                            <button className="expand-button" onClick={toggleChatbot}>⮟</button>
                        </div>
                    </div>
                    <div className={`chatbot-body ${isOpen ? 'open' : ''}`}>
                        <div className="chatbot-messages">
                            {messages.map((message, index) => (
                                <div key={index} className={`chatbot-message ${message.type}`}>
                                    {renderFormattedMessage(message.text)}
                                </div>
                            ))}
                            {isTyping && (
                                <div className="typing-indicator">
                                    <span role="img" aria-label="AI bot">🤖</span> Typing...
                                </div>
                            )}
                        </div>
                        <form className="chatbot-form" onSubmit={handleSubmit}>
                            <input
                                type="text"
                                className="chatbot-input"
                                value={input}
                                onChange={handleInputChange}
                                placeholder="Type your questions..."
                            />
                            <button type="submit">Ask</button>
                        </form>
                    </div>
                </>
            )}
        </div>
    );
}

export default Chatbot;